import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { loginRequest } from '../api';

interface AuthState {
  isAuthenticated: boolean;
  user: any | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: false,
  error: null,
};

// 检查本地存储中的认证状态
const checkAuthFromStorage = (): Partial<AuthState> => {
  const token = localStorage.getItem('token');
  const userInfo = localStorage.getItem('userInfo');
  const userStatus = localStorage.getItem('userStatus');

  if (token && userInfo && userStatus === 'login') {
    try {
      const user = JSON.parse(userInfo);
      return {
        isAuthenticated: true,
        user,
        token,
      };
    } catch (error) {
      console.error('解析用户信息失败:', error);
      // 清除无效数据
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('userStatus');
    }
  }

  return {
    isAuthenticated: false,
    user: null,
    token: null,
  };
};

// 初始化状态
const storedAuth = checkAuthFromStorage();
initialState.isAuthenticated = storedAuth.isAuthenticated || false;
initialState.user = storedAuth.user || null;
initialState.token = storedAuth.token || null;

// 登录异步action
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await loginRequest({ username, password });

      // 处理头像字段映射
      if (response.data?.member?.avatarUrl && !response.data.member.avatar) {
        response.data.member.avatar = response.data.member.avatarUrl;
      }

      const { access_token, member } = response.data as any;

      // 保存到localStorage
      localStorage.setItem('token', access_token);
      localStorage.setItem('userInfo', JSON.stringify(member));
      localStorage.setItem('userStatus', 'login');

      return {
        token: access_token,
        user: member,
      };
    } catch (error: any) {
      console.error('登录失败:', error);
      return rejectWithValue(error.response.data.message || '登录失败，请检查用户名和密码');
    }
  }
);

// 登出异步action
export const logout = createAsyncThunk('auth/logout', async () => {
  // 清除本地存储
  localStorage.removeItem('token');
  localStorage.removeItem('userInfo');
  localStorage.removeItem('userStatus');
});

// 检查认证状态
export const checkAuthStatus = createAsyncThunk('auth/checkStatus', async () => {
  const storedAuth = checkAuthFromStorage();
  return storedAuth;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // 同步检查认证状态（用于组件初始化）
    setAuthFromStorage: (state) => {
      const storedAuth = checkAuthFromStorage();
      state.isAuthenticated = storedAuth.isAuthenticated || false;
      state.user = storedAuth.user || null;
      state.token = storedAuth.token || null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 登录
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<{ token: string; user: any }>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = action.payload as string;
      })
      // 登出
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = null;
        state.loading = false;
      })
      // 检查认证状态
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        const storedAuth = action.payload;
        state.isAuthenticated = storedAuth.isAuthenticated || false;
        state.user = storedAuth.user || null;
        state.token = storedAuth.token || null;
      });
  },
});

export const { clearError, setAuthFromStorage } = authSlice.actions;
export default authSlice.reducer;
